import { Input as AntInput } from "antd";
import { clsx } from "clsx";
import { FC } from "react";
import { LabelBlock } from "../LabelBlock";
import "./TextInput.css";

/**
 * Base props for the TextInput component
 */
export interface TextInputProps {
  /**
   * Visual style variant of the input
   * @default "filled"
   */
  variant?: "outline" | "filled";

  /**
   * The current value of the input
   */
  value?: string;

  /**
   * Callback fired when the value changes
   * @param value - The new value
   */
  onChange?: (value: string) => void;

  /**
   * Placeholder text when the field is empty
   */
  placeholder?: string;

  /**
   * Whether the field is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the field is in an error state
   * @default false
   */
  isError?: boolean;

  /**
   * Whether the field is visually highlighted (sky-blue) — e.g. to flag a value
   * that differs from its default. Mirrors the `highlighted` state on `Counter`
   * and `MultiSegmentControl` so the physical-exam inputs read as one set.
   * @default false
   */
  highlighted?: boolean;

  /**
   * Field label
   */
  label?: string;

  /**
   * Description text
   */
  description?: string;

  /**
   * Width of the field as a percentage
   * @default "100%"
   */
  widthPercent?: string;

  /**
   * The size of the input
   * @default "default"
   */
  size?: "small" | "default" | "large";

  /**
   * Text to display before the value
   */
  prefix?: string | React.ReactNode;

  /**
   * Text to display after the value
   */
  suffix?: string | React.ReactNode;

  /**
   * HTML input type
   * @default "text"
   */
  type?: "text" | "email" | "password" | "url" | "tel" | "search";

  /**
   * Maximum length of input value
   */
  maxLength?: number;

  /**
   * Test ID for testing purposes
   */
  "data-testid"?: string;

  /**
   * Accessible name for the input, when there's no visible `label`.
   */
  "aria-label"?: string;

  /**
   * Callback fired when the field is focused
   */
  onFocus?: () => void;

  /**
   * Callback fired when the field is blurred
   */
  onBlur?: () => void;

  /**
   * Callback fired when Enter key is pressed
   */
  onPressEnter?: () => void;

  /**
   * Whether to show a clear button when the field has content
   * @default false
   */
  allowClear?: boolean;
}

/**
 * TextInput Component
 *
 * A single-line text input component that wraps Ant Design's Input
 * with custom styling to match the indigo design system.
 *
 * Features:
 * - Single-line text input for various types (text, email, password, etc.)
 * - Error state styling
 * - Multiple size variants
 * - Prefix/suffix display
 * - Optional clear button
 * - Accessible labeling options
 *
 * @example
 * // Basic usage
 * <TextInput
 *   label="Email"
 *   value={email}
 *   onChange={setEmail}
 *   type="email"
 *   placeholder="Enter your email"
 * />
 *
 * @example
 * // With prefix
 * <TextInput
 *   label="Website"
 *   value={url}
 *   onChange={setUrl}
 *   type="url"
 *   prefix="https://"
 * />
 *
 * @example
 * // Password input
 * <TextInput
 *   label="Password"
 *   value={password}
 *   onChange={setPassword}
 *   type="password"
 * />
 *
 * @example
 * // Outline variant
 * <TextInput
 *   variant="outline"
 *   value={value}
 *   onChange={setValue}
 * />
 */
export const TextInput: FC<TextInputProps> = ({
  variant = "filled",
  value,
  onChange,
  disabled = false,
  placeholder,
  isError = false,
  highlighted = false,
  label,
  description,
  widthPercent,
  size = "default",
  prefix,
  suffix,
  type = "text",
  maxLength,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
  onFocus,
  onBlur,
  onPressEnter,
  allowClear = false,
}) => {
  // Map size prop to Ant Design size values
  const antSize =
    size === "small" ? "small" : size === "large" ? "large" : "middle";

  // Handle onChange from Ant Design Input (event-based)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  // Handle Enter key press
  const handlePressEnter = () => {
    onPressEnter?.();
  };

  // Render password input separately
  if (type === "password") {
    return (
      <LabelBlock
        label={label}
        disabled={disabled}
        isError={isError}
        description={description}
        widthPercent={widthPercent}
      >
        <AntInput.Password
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          size={antSize}
          prefix={prefix}
          suffix={suffix}
          maxLength={maxLength}
          onFocus={onFocus}
          onBlur={onBlur}
          onPressEnter={handlePressEnter}
          allowClear={allowClear}
          data-testid={dataTestId}
          aria-label={ariaLabel}
          className={clsx(
            "indigo-textinput",
            variant === "filled" && "indigo-textinput--filled",
            variant === "outline" && "indigo-textinput--outline",
            size === "small" && "indigo-textinput--small",
            size === "large" && "indigo-textinput--large",
            isError && "indigo-textinput--error",
            highlighted && "indigo-textinput--highlighted",
            disabled && "indigo-textinput--disabled",
          )}
          style={{ width: "100%" }}
        />
      </LabelBlock>
    );
  }

  return (
    <LabelBlock
      label={label}
      disabled={disabled}
      isError={isError}
      description={description}
      widthPercent={widthPercent}
    >
      <AntInput
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        type={type}
        size={antSize}
        prefix={prefix}
        suffix={suffix}
        maxLength={maxLength}
        onFocus={onFocus}
        onBlur={onBlur}
        onPressEnter={handlePressEnter}
        allowClear={allowClear}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        className={clsx(
          "indigo-textinput",
          variant === "filled" && "indigo-textinput--filled",
          variant === "outline" && "indigo-textinput--outline",
          size === "small" && "indigo-textinput--small",
          size === "large" && "indigo-textinput--large",
          isError && "indigo-textinput--error",
          highlighted && "indigo-textinput--highlighted",
          disabled && "indigo-textinput--disabled",
        )}
        style={{ width: "100%" }}
      />
    </LabelBlock>
  );
};

export default TextInput;
